import React, { useContext, useEffect, useState } from "react";
import { eL1_STAGES, eL1_STAGES_NAME } from "./l1-form";
import { GetUnitTimeLineDetails } from "../../../utils/api.constant";
import { TContext, TResponseModel } from "../../../types/config";
import { UserContext } from "../../../context";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { eOVERALLISTSTATUS, eResultCode } from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { Col, Row, Spin, Statistic, Table, Tag } from "antd";
import DateUtils from "../../../utils/date";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";

type TProps = {
  id: number;
  showHeader?: boolean;
};

type TTimeLineDetail = {
  id: number;
  customerUploadDetailId: number;
  stageId: eL1_STAGES;
  description: string;
  createdOn: string; // ISO date-time string
  createdBy: number;
  createdByName: string;
  updatedBy: number;
  isDeleted: number;
  deletedBy: number;
};

type TUnitTimeline = {
  serialNumber: string;
  fireId: string;
  currentStageName: string;
  customerRecordNo: string;
  currentStageId: number;
  timeLineDetails: TTimeLineDetail[];
};

function TimeLine({ id, showHeader = false }: TProps) {
  const [timeLineView, setTimeLineView] = useState<TUnitTimeline | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const context: TContext = useContext(UserContext);
  const { post } = useFetch();
  const { onShowToast } = useToast();

  useEffect(() => {
    if (id) {
      fetchUnitTimeLineDetails();
    }
  }, [id]);

  const getStatusColor = (status: eOVERALLISTSTATUS) => {
    switch (status) {
      case eOVERALLISTSTATUS.NOT_SCANNED:
        return "#B0BEC5";
      case eOVERALLISTSTATUS.SCANNED_WAITING_EVAL:
        return "#FFB74D";
      case eOVERALLISTSTATUS.EVAL_L1:
        return "#42A5F5";
      case eOVERALLISTSTATUS.EVAL_L2:
        return "#7E57C2";
      case eOVERALLISTSTATUS.NO_FAULT:
        return "#66BB6A";
      case eOVERALLISTSTATUS.TEAR_DOWN:
        return "#EF5350";
      case eOVERALLISTSTATUS.ALL:
        return "#78909C";
      default:
        return "#9E9E9E";
    }
  };

  const fetchUnitTimeLineDetails = async () => {
    try {
      setLoading(true);

      const payload = {
        data: {
          id: id,
        },
      };

      const res: TResponseModel = await post(GetUnitTimeLineDetails, payload);
      if (res.dataResponse.returnCode === eResultCode.SUCCESS) {
        setTimeLineView(res.data);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: res.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: `Failed to load timeline details - ${error}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={isLoading}>
      {showHeader && timeLineView?.fireId && timeLineView?.serialNumber ? (
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="FiRe #"
              valueRender={() => <span>{timeLineView?.fireId}</span>}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Serial #"
              valueRender={() => <span>{timeLineView?.serialNumber}</span>}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Current Stage"
              valueRender={() => (
                <Tag
                  color={getStatusColor(timeLineView?.currentStageId as any)}
                >
                  {eOVERALLISTSTATUS[timeLineView?.currentStageId]?.replaceAll(
                    "_",
                    " "
                  )}
                </Tag>
              )}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Customer Record No."
              valueRender={() => (
                <span>{timeLineView?.customerRecordNo || "-"}</span>
              )}
            />
          </Col>
        </Row>
      ) : null}
      {timeLineView?.timeLineDetails &&
      timeLineView.timeLineDetails.length > 0 ? (
        <Table
          style={{ marginTop: "1rem", maxHeight: "50vh", overflow: "scroll" }}
          dataSource={timeLineView.timeLineDetails}
          columns={[
            {
              title: "Sr. No.",
              key: "srNo",
              render: (text, record, index) => index + 1,
              width: 100,
            },
            {
              title: "Stage Name (during the activity)",
              dataIndex: "stageId",
              key: "stageId",
              render: (stageId: eL1_STAGES) => (
                <Tag color={getStatusColor(stageId as any)}>
                  {eOVERALLISTSTATUS[stageId]?.replaceAll("_", " ")}
                </Tag>
              ),
            },
            {
              title: "Description",
              dataIndex: "description",
              key: "description",
              width: 350,
            },
            {
              title: "Updated By",
              dataIndex: "createdByName",
              key: "createdByName",
            },
            {
              title: "Updated on",
              dataIndex: "createdOn",
              key: "createdOn",
              // width: 200,
              render: (text) =>
                DateUtils.format(text, context?.user?.dateFormat, true),
            },
          ]}
          pagination={false}
          scroll={{ x: "max-content" }}
          size="small"
        />
      ) : (
        <span>No timeline history available.</span>
      )}
    </Spin>
  );
}

export default TimeLine;
